import { toast } from 'react-hot-toast';

/**
 * Centralized error handling utility
 */

// Error types
export const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Determine error type from HTTP status code
 */
export const getErrorType = (status) => {
  if (!status) return ERROR_TYPES.NETWORK;
  
  if (status >= 400 && status < 500) {
    switch (status) {
      case 401:
        return ERROR_TYPES.AUTHENTICATION;
      case 403:
        return ERROR_TYPES.AUTHORIZATION;
      case 422:
        return ERROR_TYPES.VALIDATION;
      default:
        return ERROR_TYPES.VALIDATION;
    }
  }
  
  if (status >= 500) {
    return ERROR_TYPES.SERVER;
  }
  
  return ERROR_TYPES.UNKNOWN;
};

/**
 * Get error severity based on type and status
 */
export const getErrorSeverity = (type, status) => {
  switch (type) {
    case ERROR_TYPES.AUTHENTICATION:
    case ERROR_TYPES.AUTHORIZATION:
      return ERROR_SEVERITY.HIGH;
    case ERROR_TYPES.SERVER:
      return status >= 500 ? ERROR_SEVERITY.CRITICAL : ERROR_SEVERITY.HIGH;
    case ERROR_TYPES.NETWORK:
      return ERROR_SEVERITY.MEDIUM;
    case ERROR_TYPES.VALIDATION:
      return ERROR_SEVERITY.LOW;
    default:
      return ERROR_SEVERITY.MEDIUM;
  }
};

/**
 * Format error message for user display
 */
export const formatErrorMessage = (error) => {
  // Default messages for different error types
  const defaultMessages = {
    [ERROR_TYPES.NETWORK]: 'Network error. Please check your connection and try again.',
    [ERROR_TYPES.AUTHENTICATION]: 'Authentication failed. Please log in again.',
    [ERROR_TYPES.AUTHORIZATION]: 'You do not have permission to perform this action.',
    [ERROR_TYPES.SERVER]: 'Server error. Please try again later.',
    [ERROR_TYPES.VALIDATION]: 'Please check your input and try again.',
    [ERROR_TYPES.UNKNOWN]: 'An unexpected error occurred. Please try again.'
  };

  // Extract message from error object
  let message = '';
  
  if (typeof error === 'string') {
    message = error;
  } else if (error?.response?.data?.message) {
    message = error.response.data.message;
  } else if (error?.message) {
    message = error.message;
  }

  // If no specific message, use default based on error type
  if (!message && error?.response?.status) {
    const errorType = getErrorType(error.response.status);
    message = defaultMessages[errorType];
  }

  return message || defaultMessages[ERROR_TYPES.UNKNOWN];
};

/**
 * Main error handler function
 */
export const handleError = (error, options = {}) => {
  const {
    showToast = true,
    logError = true,
    severity = null,
    context = '',
    onError = null
  } = options;

  // Determine error details
  const status = error?.response?.status;
  const errorType = getErrorType(status);
  const errorSeverity = severity || getErrorSeverity(errorType, status);
  const message = formatErrorMessage(error);

  // Log error for debugging
  if (logError) {
    const errorInfo = {
      message,
      type: errorType,
      severity: errorSeverity,
      status,
      context,
      timestamp: new Date().toISOString(),
      url: error?.config?.url,
      method: error?.config?.method,
      stack: error?.stack,
    };

    console.error('Error handled:', errorInfo);

    // In production, you might want to send this to a logging service
    // logToService(errorInfo);
  }

  // Show user notification
  if (showToast) {
    switch (errorSeverity) {
      case ERROR_SEVERITY.CRITICAL:
      case ERROR_SEVERITY.HIGH:
        toast.error(message, { duration: 6000 });
        break;
      case ERROR_SEVERITY.MEDIUM:
        toast.error(message, { duration: 4000 });
        break;
      case ERROR_SEVERITY.LOW:
        toast.error(message, { duration: 3000 });
        break;
      default:
        toast.error(message);
    }
  }

  // Handle authentication errors
  if (errorType === ERROR_TYPES.AUTHENTICATION) {
    // Clear authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    
    // Redirect to login (but avoid infinite redirects)
    if (!window.location.pathname.includes('/login')) {
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
  }

  // Call custom error handler if provided
  if (onError) {
    onError(error, { type: errorType, severity: errorSeverity, message });
  }

  return {
    type: errorType,
    severity: errorSeverity,
    message,
    status
  };
};

/**
 * Wrapper for async operations with error handling
 */
export const withErrorHandling = (asyncFn, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const errorInfo = handleError(error, options);
      throw errorInfo; // Re-throw with processed error info
    }
  };
};

/**
 * React hook for error handling
 */
export const useErrorHandler = () => {
  return (error, options = {}) => handleError(error, options);
};